import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { UploadCloud, Trash2, Loader2, Plus, Save, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TARGET_OPTIONS = [
  { value: "in-the-field", label: "Home · 'In the field' grid" },
  { value: "case-study", label: "Case study gallery (needs slug)" },
  { value: "canada-chapter", label: "About · Canada Chapter mosaic" },
];

const CASE_STUDY_SLUGS = [
  { value: "carpark-to-8000", label: "Carpark to 8,000+" },
  { value: "orientation-scale", label: "Orientation at scale" },
  { value: "calgary-pride", label: "Calgary Pride" },
  { value: "toronto-eventpro", label: "Toronto EventPro" },
  { value: "monash-openday", label: "Monash Open Day" },
  { value: "spreadsheet-to-systems", label: "Spreadsheets to systems" },
];

const BLANK = {
  url: "",
  caption: "",
  meta: "",
  event: "",
  year: String(new Date().getFullYear()),
  location: "",
  target: "in-the-field",
  slug: "",
  order: 0,
  published: true,
};

export default function AdminEventPhotos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/event-photos`, { withCredentials: true });
      setItems(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load photos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Upload → open editor pre-filled with the returned URL
  const uploadAndEdit = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/media/upload`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data?.url;
      if (!url) throw new Error("No URL returned");
      setCreating(true);
      setEditing({
        ...BLANK,
        url,
        caption: file.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " "),
        order: items.length + 1,
      });
      toast.success("Uploaded — now tag it");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (payload) => {
    try {
      if (payload.id) {
        const { id, ...body } = payload;
        await axios.patch(`${API}/admin/event-photos/${id}`, body, { withCredentials: true });
        toast.success("Updated");
      } else {
        await axios.post(`${API}/admin/event-photos`, payload, { withCredentials: true });
        toast.success("Created");
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(x => x.msg).join(", ") : d || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event photo?")) return;
    try {
      await axios.delete(`${API}/admin/event-photos/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((c) => c.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-event-photos">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow">Content</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl leading-tight tracking-tight">Event photos</h1>
          <p className="mt-3 text-[#A9A9A6] text-sm max-w-lg">
            Drop an image, tag it with event / year / location, choose which page it appears on.
            Merges with the built-in fallback set on the front-end. The Home
            "In the field" strip shows up to <strong className="text-[#FAF9F6]">5 photos</strong>;
            server-uploaded photos take the first slots, fallback fills the rest.
          </p>
        </div>
        <button
          onClick={() => { setCreating(true); setEditing({ ...BLANK, order: items.length + 1 }); }}
          data-testid="event-photo-add"
          className="btn-primary"
        >
          <Plus size={14} /> New (URL only)
        </button>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          uploadAndEdit(Array.from(e.dataTransfer.files || []));
        }}
        onClick={() => inputRef.current?.click()}
        data-testid="event-photo-dropzone"
        className={`mt-6 border-2 border-dashed p-8 lg:p-10 text-center cursor-pointer transition-colors ${
          dragOver ? "border-[#D8A936] bg-[#D8A936]/10" : "border-[#343437] bg-[#151517]/60 hover:border-[#6E6E6B]"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => uploadAndEdit(Array.from(e.target.files || []))}
          data-testid="event-photo-file-input"
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-3 text-[#D8A936]">
            <Loader2 size={16} className="animate-spin" />
            <span className="text-sm">Uploading…</span>
          </div>
        ) : (
          <div>
            <UploadCloud size={28} className="mx-auto text-[#D8A936] mb-3" />
            <p className="text-sm text-[#FAF9F6]">Drop a photo here or click to browse</p>
            <p className="text-[10px] tracking-[0.16em] uppercase text-[#6E6E6B] mt-2">
              Max 15MB · You'll be asked to tag it after upload
            </p>
          </div>
        )}
      </div>

      {loading ? (
        <div className="mt-10 text-[#A9A9A6]">Loading…</div>
      ) : (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.length === 0 && (
            <div className="col-span-full border border-[#343437] p-8 text-[#A9A9A6] text-sm text-center">
              No custom event photos yet. The site is using the built-in fallback set.
            </div>
          )}
          {items.map((p) => (
            <div
              key={p.id}
              className="border border-[#343437] bg-[#151517]/60 overflow-hidden group"
              data-testid={`event-photo-row-${p.id}`}
            >
              <div className="aspect-[4/3] bg-[#0B0B0C] relative">
                {p.url ? (
                  <img
                    src={p.url}
                    alt={p.caption}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon size={28} className="absolute inset-0 m-auto text-[#343437]" />
                )}
                <span className="absolute top-2 right-2 text-[9px] tracking-[0.16em] uppercase bg-[#0B0B0C]/85 border border-[#D8A936]/60 text-[#D8A936] px-2 py-0.5">
                  {p.target}{p.slug ? ` · ${p.slug}` : ""}
                </span>
              </div>
              <div className="p-4">
                <p className="text-sm text-[#FAF9F6] leading-tight">{p.caption || "—"}</p>
                <p className="text-[10px] text-[#A9A9A6] mt-1">
                  {[p.event, p.year, p.location].filter(Boolean).join(" · ")}
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => setEditing(p)}
                    data-testid={`event-photo-edit-${p.id}`}
                    className="px-3 py-1.5 border border-[#343437] text-[10px] tracking-widest uppercase hover:border-[#D8A936] hover:text-[#D8A936] transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(p.id)}
                    data-testid={`event-photo-delete-${p.id}`}
                    className="p-1.5 border border-[#343437] hover:border-red-400 hover:text-red-400 transition-colors"
                    aria-label="Delete"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditorModal
          initial={editing}
          isNew={creating}
          onCancel={() => { setEditing(null); setCreating(false); }}
          onSave={save}
        />
      )}
    </div>
  );
}

function EditorModal({ initial, isNew, onCancel, onSave }) {
  const [form, setForm] = useState({ ...initial });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    if (form.target === "case-study" && !form.slug) {
      toast.error("Pick a case study slug or change the target.");
      return;
    }
    onSave({ ...form, order: Number(form.order) || 0 });
  };
  return (
    <div className="fixed inset-0 z-[80] bg-[#0B0B0C]/85 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-auto">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#151517] border border-[#343437] w-full max-w-2xl p-8 space-y-5 relative my-8"
        data-testid="event-photo-editor"
      >
        <button type="button" onClick={onCancel} className="absolute top-4 right-4 text-[#A9A9A6] hover:text-[#FAF9F6]" aria-label="Close">
          <X size={18} />
        </button>
        <p className="eyebrow">{isNew ? "New" : "Edit"}</p>
        <h2 className="font-display text-2xl">Event photo</h2>

        {form.url && (
          <div className="border border-[#343437] bg-[#0B0B0C] p-2">
            <img src={form.url} alt="preview" className="max-h-56 mx-auto" />
          </div>
        )}

        <TextField label="URL" value={form.url} onChange={(v) => set("url", v)} required testid="event-photo-url" />
        <TextField label="Caption (visible on card)" value={form.caption} onChange={(v) => set("caption", v)} required testid="event-photo-caption" />
        <TextField label="Meta (small line under caption)" value={form.meta} onChange={(v) => set("meta", v)} testid="event-photo-meta" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TextField label="Event" value={form.event} onChange={(v) => set("event", v)} testid="event-photo-event" />
          <TextField label="Year" value={form.year} onChange={(v) => set("year", v)} testid="event-photo-year" />
          <TextField label="Location" value={form.location} onChange={(v) => set("location", v)} testid="event-photo-location" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="eyebrow block mb-2">Target surface</span>
            <select
              value={form.target}
              onChange={(e) => set("target", e.target.value)}
              data-testid="event-photo-target"
              className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2.5 outline-none focus:border-[#D8A936]"
            >
              {TARGET_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className="bg-[#151517]">{o.label}</option>
              ))}
            </select>
          </label>
          {form.target === "case-study" && (
            <label className="block">
              <span className="eyebrow block mb-2">Case study</span>
              <select
                value={form.slug}
                onChange={(e) => set("slug", e.target.value)}
                data-testid="event-photo-slug"
                className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2.5 outline-none focus:border-[#D8A936]"
              >
                <option value="" className="bg-[#151517]">— select —</option>
                {CASE_STUDY_SLUGS.map((s) => (
                  <option key={s.value} value={s.value} className="bg-[#151517]">{s.label}</option>
                ))}
              </select>
            </label>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Order" type="number" value={form.order} onChange={(v) => set("order", v)} testid="event-photo-order" />
          <label className="flex items-center gap-3 text-sm text-[#FAF9F6] pt-6">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => set("published", e.target.checked)}
              className="accent-[#D8A936]"
              data-testid="event-photo-published"
            />
            Published
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
          <button type="submit" data-testid="event-photo-save" className="btn-primary">
            <Save size={14} /> {isNew ? "Create" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}{required && <span className="text-[#D8A936]"> *</span>}</span>
      <input
        type={type}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2.5 outline-none focus:border-[#D8A936]"
      />
    </label>
  );
}
